// ═══════════════════════════════════════════════════════════════════════════════
// Task Resubmit Page (Helper)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import { PhotoUploader, GpsToggle, OsmEmbedInteractiveMap } from '../../components/task';
import { ReceiptImageLightbox } from '../../components/receipt/ReceiptImageLightbox';
import { useToast } from '../../components/ui/ToastProvider';
import { CommentThread, UnsentCommentOnSubmitDialog, type CommentThreadHandle } from '../../components/comments';
import { geocodingApi, taskInstanceApi } from '../../lib/api';
import { isLimitReachedError } from '../../lib/limitError';
import { navigateBack } from '../../lib/navigateBack';
import { uploadTaskPhotoViaBackend } from '../../lib/directPhotoUpload';
import { useOfflineSync } from '../../contexts/OfflineSyncContext';
import { readFileAsDataUrl } from '../../offline/dataUrl';
import { enqueueTaskResubmit } from '../../offline/offlineQueue';
import {
  LEVEL1_DEFAULTS,
  getTodayDate,
  APPROVAL_STATUS,
  COMMENT_TARGET_TYPE,
  TASK_KIND,
  type TaskInstanceWithSubmission,
} from '@fdh/shared';
import { isValidGpsCoordinatePair } from '../../lib/osmStaticMap';
import { getHelperTodayYmd, resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { isWithinHelperEarlySubmitRange } from '../../utils/taskEarlySubmit';
import { TaskSubmitConfirmModal } from '../../components/task';
import {
  AutoTranslationHint,
  resolveInstanceDisplayNeedsUpdateReason,
  resolveTaskDisplayDescription,
  resolveTaskDisplayTitle,
  resolveTranslationTargetLanguageLabel,
  taskInstanceHasHelperTranslations,
} from '../../components/translation';

const getNowDatetimeInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  return `${year}-${month}-${day} ${hour}:${minute}`;
};

const splitDatetime = (value: string): { date: string; time: string } => {
  const [datePart = '', timePart = ''] = value.split(' ');
  return { date: datePart, time: timePart };
};

export const TaskResubmitPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { refreshPendingCount } = useOfflineSync();

  const [taskInstance, setTaskInstance] = useState<TaskInstanceWithSubmission | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<string[]>([]);
  const [existingPhotoDisplayUrls, setExistingPhotoDisplayUrls] = useState<string[]>([]);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsLat, setGpsLat] = useState<number | ''>('');
  const [gpsLng, setGpsLng] = useState<number | ''>('');
  const [gpsAddress, setGpsAddress] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [completedTime, setCompletedTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsentCommentModalOpen, setUnsentCommentModalOpen] = useState(false);
  const [submitConfirmModalOpen, setSubmitConfirmModalOpen] = useState(false);
  const [unsentCommentDialogBusy, setUnsentCommentDialogBusy] = useState(false);
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);
  const commentThreadRef = useRef<CommentThreadHandle | null>(null);

  useEffect(() => {
    setShowTaskContentOriginal(false);
  }, [language]);

  const fetchTaskInstance = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await taskInstanceApi.get(id, language);
      if (response.success && response.data) {
        const data = response.data;

        // Validate status
        if (data.status !== APPROVAL_STATUS.NEEDS_UPDATE) {
          setError('This task cannot be resubmitted');
          setLoading(false);
          return;
        }

        if (data.task.taskKind === TASK_KIND.INVENTORY) {
          navigate(`/tasks/inventory/${id}`, { replace: true });
          setLoading(false);
          return;
        }

        setTaskInstance(data);

        // Pre-fill with previous submission data
        if (data.submission) {
          setExistingPhotoUrls(data.submission.photoUrls);
          setExistingPhotoDisplayUrls(data.submission.photoUrls);
          setGpsEnabled(data.submission.gpsEnabled);
          setGpsLat(data.submission.gpsLat);
          setGpsLng(data.submission.gpsLng);
          const submittedCompleted = splitDatetime(data.submission.completedAt);
          setCompletedDate(submittedCompleted.date);
          setCompletedTime(submittedCompleted.time);
        }
      } else {
        setError(response.error ?? t('task.review.loadError'));
      }
    } catch {
      setError(t('task.review.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, language, navigate, t]);

  useEffect(() => {
    fetchTaskInstance();
  }, [fetchTaskInstance]);

  useEffect(() => {
    if (!id || !taskInstance || !user) return;
    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    if (
      taskInstance.dueDate > todayYmd &&
      !isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
    ) {
      navigate(`/task-instances/${id}`, { replace: true });
    }
  }, [id, taskInstance, user, navigate]);

  useEffect(() => {
    if (completedDate.trim() !== '' && completedTime.trim() !== '') return;
    const timezone = resolveDisplayTimeZone(user?.settings.timezone);
    const now = splitDatetime(getNowDatetimeInTimezone(timezone));
    setCompletedDate(now.date);
    setCompletedTime(now.time);
  }, [completedDate, completedTime, user?.settings.timezone]);

  const handleGpsToggle = (enabled: boolean, lat: number | '', lng: number | '') => {
    setGpsEnabled(enabled);
    setGpsLat(lat);
    setGpsLng(lng);
  };

  const gpsMapCoords = useMemo(() => {
    if (!gpsEnabled || gpsLat === '' || gpsLng === '') {
      return null;
    }
    if (!isValidGpsCoordinatePair(gpsLat, gpsLng)) {
      return null;
    }
    return { lat: gpsLat, lng: gpsLng };
  }, [gpsEnabled, gpsLat, gpsLng]);

  useEffect(() => {
    const lat = typeof gpsLat === 'number' ? gpsLat : null;
    const lng = typeof gpsLng === 'number' ? gpsLng : null;
    if (!gpsEnabled || lat === null || lng === null) {
      setGpsAddress('');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await geocodingApi.reverse(lat, lng, language);
        if (cancelled) return;
        if (response.success && response.data?.displayName) {
          setGpsAddress(response.data.displayName);
          return;
        }
        setGpsAddress('');
      } catch {
        if (!cancelled) {
          setGpsAddress('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gpsEnabled, gpsLat, gpsLng, language]);

  const uploadPhotosToGcs = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      try {
        const url = await uploadTaskPhotoViaBackend(file, file.name || 'photo.jpg');
        urls.push(url);
      } catch {
        throw new Error('PHOTO_UPLOAD_FAILED');
      }
    }
    return urls;
  };

  const performTaskResubmit = useCallback(async () => {
    if (!id) {
      return;
    }

    const totalPhotos = photos.length + existingPhotoUrls.length;
    const requirePhoto = Boolean(taskInstance?.task.requirePhoto);
    if (requirePhoto && totalPhotos < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS) {
      setError(t('task.complete.photoRequired'));
      return;
    }
    if (!completedDate || !completedTime) {
      setError(t('task.complete.completedAtRequired'));
      return;
    }
    const completedAt = `${completedDate} ${completedTime}`;

    setSubmitting(true);
    setError(null);

    try {
      if (!navigator.onLine) {
        const newPhotoUrls =
          photos.length > 0 ? await Promise.all(photos.map((f) => readFileAsDataUrl(f))) : [];
        const allPhotoUrls = [...existingPhotoUrls, ...newPhotoUrls].slice(
          0,
          LEVEL1_DEFAULTS.MAX_TASK_PHOTOS
        );
        await enqueueTaskResubmit({
          taskInstanceId: id,
          payload: {
            photoUrls: allPhotoUrls,
            gpsLat,
            gpsLng,
            gpsEnabled,
            note: '',
            completedAt,
          },
        });
        await refreshPendingCount();
        showSuccessToast(t('toast.offlineQueued'));
        navigate('/today', { replace: true });
        return;
      }

      const newPhotoUrls = photos.length > 0 ? await uploadPhotosToGcs(photos) : [];
      const allPhotoUrls = [...existingPhotoUrls, ...newPhotoUrls].slice(
        0,
        LEVEL1_DEFAULTS.MAX_TASK_PHOTOS
      );

      const response = await taskInstanceApi.resubmit(id, {
        photoUrls: allPhotoUrls,
        gpsLat,
        gpsLng,
        gpsEnabled,
        note: '',
        completedAt,
      });

      if (response.success) {
        if (response.data) {
          setTaskInstance(response.data);
        }
        showSuccessToast(t('toast.task.resubmitSuccess'));
        navigate('/today', { replace: true });
      } else {
        setError(response.error ?? t('task.complete.submitError'));
      }
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      setError(
        caught instanceof Error && caught.message === 'PHOTO_UPLOAD_FAILED'
          ? t('task.complete.photoUploadError')
          : t('task.complete.submitError')
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    id,
    photos,
    existingPhotoUrls,
    gpsEnabled,
    gpsLat,
    gpsLng,
    navigate,
    t,
    refreshPendingCount,
    showSuccessToast,
    completedDate,
    completedTime,
    taskInstance?.task.requirePhoto,
  ]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!id) {
        return;
      }

      const totalPhotos = photos.length + existingPhotoUrls.length;
      const requirePhoto = Boolean(taskInstance?.task.requirePhoto);
      if (requirePhoto && totalPhotos < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS) {
        setError(t('task.complete.photoRequired'));
        return;
      }
      if (!completedDate || !completedTime) {
        setError(t('task.complete.completedAtRequired'));
        return;
      }

      if (commentThreadRef.current?.hasUnsentDraft()) {
        setUnsentCommentModalOpen(true);
        return;
      }

      const todayYmd = getHelperTodayYmd(user?.settings?.timezone);
      if (
        taskInstance &&
        taskInstance.dueDate > todayYmd &&
        isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
      ) {
        setSubmitConfirmModalOpen(true);
        return;
      }

      void performTaskResubmit();
    },
    [
      id,
      photos.length,
      existingPhotoUrls.length,
      completedDate,
      completedTime,
      performTaskResubmit,
      taskInstance,
      user?.settings?.timezone,
      t,
    ],
  );

  const handleCloseSubmitConfirmModal = () => {
    if (submitting) {
      return;
    }
    setSubmitConfirmModalOpen(false);
  };

  const handleConfirmSubmitModal = () => {
    setSubmitConfirmModalOpen(false);
    void performTaskResubmit();
  };

  const handleUnsentCommentBackToEdit = useCallback(() => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentModalOpen(false);
    commentThreadRef.current?.openComposer();
  }, [unsentCommentDialogBusy]);

  const handleUnsentCommentSubmitOnly = useCallback(() => {
    if (unsentCommentDialogBusy) {
      return;
    }
    commentThreadRef.current?.clearDraft();
    setUnsentCommentModalOpen(false);
    void performTaskResubmit();
  }, [unsentCommentDialogBusy, performTaskResubmit]);

  const handleUnsentCommentSubmitWithComment = useCallback(async () => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentDialogBusy(true);
    const sent = await commentThreadRef.current?.sendDraft();
    setUnsentCommentDialogBusy(false);
    if (sent === false) {
      return;
    }
    setUnsentCommentModalOpen(false);
    void performTaskResubmit();
  }, [unsentCommentDialogBusy, performTaskResubmit]);

  const handleRemoveExistingPhoto = (index: number) => {
    setExistingPhotoUrls((prev) => prev.filter((_, i) => i !== index));
    setExistingPhotoDisplayUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBack = () => {
    navigateBack(navigate, '/today');
  };

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (error && !taskInstance) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <p className="text-center text-red-600" role="alert">{error}</p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 text-primary-600 underline hover:no-underline focus:outline-none"
        >
          {t('task.action.back')}
        </button>
      </div>
    );
  }

  const totalPhotos = photos.length + existingPhotoUrls.length;
  const canAddMorePhotos = totalPhotos < LEVEL1_DEFAULTS.MAX_TASK_PHOTOS;

  const requirePhoto = Boolean(taskInstance?.task.requirePhoto);

  return (
    <div className="min-h-full bg-gray-50 pb-8 dark:bg-gray-950">
      {/* Header */}
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('task.resubmit.title')}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('task.resubmit.subtitle')}</p>
                {requirePhoto ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('task.resubmit.photoHint')}</p>
                ) : null}
              </div>
              {taskInstance && taskInstanceHasHelperTranslations(taskInstance) ? (
                <AutoTranslationHint
                  targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                    t,
                    taskInstance.task.translation?.targetLanguage,
                    language,
                  )}
                  showingOriginal={showTaskContentOriginal}
                  onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                />
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* Task Info */}
      {taskInstance && (
        <div className="mx-auto max-w-lg px-4 pt-4">
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 dark:border-orange-700/60 dark:bg-orange-950/35">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              {resolveTaskDisplayTitle(taskInstance.task, showTaskContentOriginal)}
            </h2>
            {taskInstance.task.description ? (
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300">
                {resolveTaskDisplayDescription(taskInstance.task, showTaskContentOriginal)}
              </p>
            ) : null}
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
              <div className="flex justify-between">
                <span>{t('task.review.taskDate')}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.dueDate}</span>
              </div>
              {taskInstance.task.dueTime ? (
                <div className="flex justify-between">
                  <span>{t('task.form.dueTimeLabel')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.task.dueTime}</span>
                </div>
              ) : null}
            </div>
            {taskInstance.needsUpdateReason && String(taskInstance.needsUpdateReason).trim() !== '' ? (
              <div
                className="mt-3 rounded-lg border border-red-200/80 bg-white/90 p-3 dark:border-red-900/50 dark:bg-red-950/25"
                role="region"
                aria-label={t('task.today.employerFeedbackLabel')}
              >
                <p className="text-xs font-semibold text-red-800 dark:text-red-200">
                  {t('task.today.employerFeedbackLabel')}
                </p>
                <p className="mt-1 text-sm text-red-900 dark:text-red-100">
                  {resolveInstanceDisplayNeedsUpdateReason(
                    taskInstance.needsUpdateReason,
                    taskInstance.translation,
                    showTaskContentOriginal,
                  )}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Form */}
      <form id="task-resubmit-form" onSubmit={handleFormSubmit} className="mx-auto max-w-lg space-y-6 px-4 py-6">
        {/* Existing Photos */}
        {existingPhotoUrls.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-medium text-gray-700">
              {requirePhoto ? t('task.complete.photoSection') : t('task.complete.submittedPhotosOnly')}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {existingPhotoUrls.map((url, index) => (
                <div key={url} className="relative aspect-square">
                  {id ? (
                    <ReceiptImageLightbox
                      taskInstancePhoto={{ taskInstanceId: id, photoIndex: index }}
                      alt={t('task.complete.photoAlt', { n: index + 1 })}
                      enlargeAriaLabel={t('task.complete.enlargePhotoAria')}
                      closeLabel={t('common.closeViewer')}
                      thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                      thumbImgClassName="h-full w-full object-cover"
                    />
                  ) : (
                    <ReceiptImageLightbox
                      previewSrc={existingPhotoDisplayUrls[index] ?? url}
                      alt={t('task.complete.photoAlt', { n: index + 1 })}
                      enlargeAriaLabel={t('task.complete.enlargePhotoAria')}
                      closeLabel={t('common.closeViewer')}
                      thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                      thumbImgClassName="h-full w-full object-cover"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingPhoto(index)}
                    disabled={submitting}
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50"
                    aria-label={t('task.complete.removePhoto')}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add new photos only when the task requires photos */}
        {canAddMorePhotos && requirePhoto ? (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <PhotoUploader
              photos={photos}
              onPhotosChange={setPhotos}
              maxPhotos={LEVEL1_DEFAULTS.MAX_TASK_PHOTOS - existingPhotoUrls.length}
              disabled={submitting}
            />
          </div>
        ) : null}

        {/* GPS Toggle + interactive OSM embed when coordinates are available */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <GpsToggle enabled={gpsEnabled} onToggle={handleGpsToggle} disabled={submitting} />
          {gpsMapCoords ? (
            <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
              <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('task.review.gpsLocation')}
              </h3>
              <OsmEmbedInteractiveMap lat={gpsMapCoords.lat} lng={gpsMapCoords.lng}>
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('task.review.addressLabel')}
                  </h4>
                  {gpsAddress ? (
                    <p className="mt-1 text-sm leading-relaxed text-gray-800 dark:text-gray-100">{gpsAddress}</p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {t('task.review.addressUnavailable')}
                    </p>
                  )}
                </div>
              </OsmEmbedInteractiveMap>
            </div>
          ) : null}
        </div>

        <div className="rounded-xl bg-white p-4 shadow-sm">
          <label className="block text-sm font-medium text-gray-700">
            {t('task.complete.completedAtLabel')}
          </label>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <IsoDatePicker
              id="completedDate"
              ariaLabel={t('task.complete.completedAtLabel')}
              value={completedDate}
              onChange={setCompletedDate}
              maxDate={getTodayDate()}
              disabled={submitting}
              valueFormat="iso"
            />
            <NativeTimeInput
              id="completedTime"
              aria-label={t('task.complete.completedAtLabel')}
              value={completedTime}
              onChange={(e) => setCompletedTime(e.target.value)}
              disabled={submitting}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-50 p-4 text-center">
            <p className="text-sm text-red-600" role="alert">{error}</p>
          </div>
        )}
      </form>

      {taskInstance && id && (
        <div className="mx-auto max-w-lg px-4 pb-6">
          <CommentThread
            ref={commentThreadRef}
            targetType={COMMENT_TARGET_TYPE.TASK_INSTANCE}
            targetId={id}
          />
        </div>
      )}

      <TaskSubmitConfirmModal
        open={submitConfirmModalOpen}
        dueDateYmd={taskInstance?.dueDate ?? ''}
        todayYmd={getHelperTodayYmd(user?.settings?.timezone)}
        t={t}
        title={t('task.submitConfirmTitle')}
        prompt={t('task.submitConfirmPrompt')}
        confirmLabel={t('task.submitConfirmAction')}
        cancelLabel={t('common.cancel')}
        submitting={submitting}
        onClose={handleCloseSubmitConfirmModal}
        onConfirm={handleConfirmSubmitModal}
      />

      <UnsentCommentOnSubmitDialog
        open={unsentCommentModalOpen}
        context="taskResubmit"
        busy={unsentCommentDialogBusy}
        onBackToEdit={handleUnsentCommentBackToEdit}
        onSubmitOnly={handleUnsentCommentSubmitOnly}
        onSubmitWithComment={handleUnsentCommentSubmitWithComment}
      />

      <div className="mx-auto max-w-lg border-t border-gray-200 bg-gray-50 px-4 py-6 dark:border-gray-700 dark:bg-gray-950">
        <button
          type="submit"
          form="task-resubmit-form"
          disabled={submitting || (requirePhoto && totalPhotos < LEVEL1_DEFAULTS.MIN_TASK_PHOTOS)}
          className="w-full rounded-xl bg-orange-600 py-4 font-medium text-white shadow-md transition-all hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? t('task.complete.submitting') : t('task.resubmit.submit')}
        </button>
      </div>
    </div>
  );
};
